import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { FileText } from "lucide-react";

// Idiomas disponíveis para exportação de PDF
export const EXPORT_LANGUAGES = {
  en: { name: 'English', flag: '🇺🇸' },
  'pt-BR': { name: 'Português (Brasil)', flag: '🇧🇷' },
  'pt-PT': { name: 'Português (Portugal)', flag: '🇵🇹' },
  es: { name: 'Español', flag: '🇪🇸' },
} as const;

export type ExportLanguage = keyof typeof EXPORT_LANGUAGES;

interface ExportLanguageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (language: ExportLanguage) => void;
}

export function ExportLanguageDialog({
  open,
  onOpenChange,
  onExport,
}: ExportLanguageDialogProps) {
  const { t, i18n } = useTranslation("common");
  const [selectedLanguage, setSelectedLanguage] = React.useState<ExportLanguage>(
    (i18n.language as ExportLanguage) || "pt-BR"
  );

  const handleExport = () => {
    onExport(selectedLanguage);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t("exportLanguage.title")}
          </DialogTitle>
          <DialogDescription>
            {t("exportLanguage.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Select
            value={selectedLanguage}
            onValueChange={(value) => setSelectedLanguage(value as ExportLanguage)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("exportLanguage.select_language")} />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(EXPORT_LANGUAGES).map(([code, { name, flag }]) => (
                <SelectItem key={code} value={code}>
                  <span className="flex items-center gap-2">
                    <span>{flag}</span>
                    <span>{name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("exportLanguage.cancel")}
          </Button>
          <Button onClick={handleExport}>
            <FileText className="h-4 w-4 mr-2" />
            {t("exportLanguage.export")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
